import React from 'react';
import { Paper, useTheme, Box, CircularProgress, Typography, Badge, Chip } from '@mui/material';
import { StaticDatePicker, PickersDay } from '@mui/x-date-pickers';
import { useGetATGPosSalesByMonthQuery } from '../../../../../features/atgs/atgsApiSlice';
import { tokens } from '../../../../../theme';

const toJsDate = (d) => (d && typeof d.toDate === 'function' ? d.toDate() : d);
const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const CustomPickersDay = (props) => {
  const { day, selected, outsideCurrentMonth, salesDaysSet, ...other } = props;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // preserve MUI behavior for outside days
  if (outsideCurrentMonth) return <PickersDay day={day} disabled />;

  // Normalize Dayjs -> Date
  const jsDay = toJsDate(day);
  const isValidDate = jsDay instanceof Date && !Number.isNaN(jsDay.getTime());

  const today = stripTime(new Date());
  const thisDay = isValidDate ? stripTime(jsDay) : null;
  const isFuture = isValidDate && thisDay > today;

  const isoKey = isValidDate ? ymd(thisDay) : '';
  const hasSales = isValidDate && Boolean(salesDaysSet?.has(isoKey));

  return (
    <Badge
      overlap="circular"
      badgeContent={hasSales ? '🟢' : '🔴'}
      sx={{ '& .MuiBadge-badge': { top: 3, right: 20 } }}
    >
      <PickersDay
        {...other}
        day={day}
        selected={selected}
        disabled={isFuture}
        sx={{
          typography: 'h6',
          fontSize: '1.1rem',
          '&.MuiPickersDay-today': { border: 'none' }, // remove default today ring
          ...(selected && {
            bgcolor: colors.primary[200],
            color: theme.palette.getContrastText(colors.primary[200]),
            boxShadow: `0 0 0 2px ${colors.primary[200]} inset, 0 0 0 2px ${colors.primary[200]}`,
            '&:hover': { bgcolor: colors.primary[200] },
          }),
        }}
      />
    </Badge>
  );
};

const ATGDateSelector = ({
  isLoading,
  selectedDate,
  handleDateChange,
  atg_name,
  shiftStartHour = 0,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const today = stripTime(new Date());

  // Clamp future selected values to today (prevents “tomorrow” initial selection)
  React.useEffect(() => {
    const sel = stripTime(toJsDate(selectedDate));
    if (sel > today) handleDateChange(today);
  }, [selectedDate, handleDateChange]);

  // Fetch POS sales for the month of selectedDate
  const { data: posSalesForMonth, isFetching: isFetchingPosSalesMonth } =
    useGetATGPosSalesByMonthQuery({
      atg_name,
      year: toJsDate(selectedDate).getFullYear(),
      month: toJsDate(selectedDate).getMonth() + 1,
      shiftStartHour,
    });

  // Build a Set('YYYY-MM-DD') of days with sales (payload grades OR a key)
  const salesDaysSet = React.useMemo(() => {
    const items = Array.isArray(posSalesForMonth)
      ? posSalesForMonth
      : posSalesForMonth?.days || [];

    const set = new Set();
    for (const item of items) {
      const dateStr = item?.date; // "YYYY-MM-DD"
      const grades = item?.payload?.state?.reported?.fuel_sales_daily?.grades;
      const hasGrades = Array.isArray(grades) && grades.length > 0;
      const hasKey = typeof item?.key === 'string' && item.key.length > 0;
      if (dateStr && (hasGrades || hasKey)) set.add(dateStr);
    }
    return set;
  }, [posSalesForMonth]);

  return (
    <Paper
      style={{
        marginTop: 0,
        padding: '10px',
        height: '500px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ borderBottom: '1px solid #ddd', pb: 2 }}>
        <Typography variant="h4" gutterBottom>
          Select a Date
        </Typography>
        <Chip
          size="small"
          label={`Today: ${today.toLocaleDateString()}`}
          sx={{ bgcolor: colors.primary[200], color: theme.palette.getContrastText(colors.primary[200]) }}
        />
      </Box>

      <Box
        sx={{
          p: '10px',
          mb: '10px',
          backgroundColor: 'primary.light',
          borderRadius: '4px',
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" color="white">
          <strong>{`Selected Date: ${toJsDate(selectedDate).toLocaleDateString()}`}</strong>
        </Typography>
      </Box>

      {isFetchingPosSalesMonth && (
        <Box sx={{ px: 1, pb: 1 }}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Loading POS sales for month…
          </Typography>
        </Box>
      )}

      <Box sx={{ height: '100%', width: '100%' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
            <Typography variant="h4" sx={{ ml: 2 }}>
              Loading Date Picker...
            </Typography>
          </Box>
        ) : (
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            openTo="day"
            value={selectedDate}
            onChange={handleDateChange}
            shouldDisableDate={(date) => stripTime(toJsDate(date)) > today}
            slots={{ day: CustomPickersDay }}
            slotProps={{
              day: { salesDaysSet },
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default ATGDateSelector;
